import { clsx } from 'clsx';
import { Icon } from './ui/Icon';
import { VideoPreview } from './VideoPreview';
import { useEditor, type ObjectType } from '@/store/editor';
import type { Asset } from '@/lib/api';

type Hotspot = { obj: ObjectType; x: number; y: number; w: number; h: number; rx?: number };

const hotspots: Hotspot[] = [
  { obj: 'sky', x: 20, y: 20, w: 540, h: 130, rx: 6 },
  { obj: 'sun', x: 550, y: 135, w: 115, h: 115, rx: 60 },
  { obj: 'mountain', x: 20, y: 185, w: 760, h: 105, rx: 6 },
  { obj: 'face', x: 368, y: 220, w: 64, h: 76, rx: 32 },
  { obj: 'person', x: 360, y: 298, w: 80, h: 148, rx: 8 },
  { obj: 'title', x: 290, y: 378, w: 220, h: 44, rx: 4 },
];

type Props = {
  asset?: Asset;
};

export function PreviewCanvas({ asset }: Props) {
  // Asset-baserad preview om vi har riktig media
  if (asset) {
    return (
      <main className="bg-bg-base flex flex-col p-5 gap-3.5 min-w-0 overflow-hidden">
        <VideoPreview asset={asset} />
      </main>
    );
  }

  return <DemoCanvas />;
}

function DemoCanvas() {
  const selectedObject = useEditor((s) => s.selectedObject);
  const setSelectedObject = useEditor((s) => s.setSelectedObject);
  const setToast = useEditor((s) => s.setToast);

  return (
    <main className="bg-bg-base flex flex-col p-5 gap-3.5 min-w-0 overflow-hidden">
      <div
        className="flex-1 bg-black rounded-xl overflow-hidden relative shadow-2xl"
        onClick={(e) => {
          if ((e.target as Element).tagName === 'svg' || (e.target as Element).classList.contains('preview-canvas-bg')) {
            setSelectedObject('default');
          }
        }}
      >
        <svg viewBox="0 0 800 450" preserveAspectRatio="xMidYMid slice" className="w-full h-full block">
          <defs>
            <linearGradient id="skygrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FDD49B" />
              <stop offset="50%" stopColor="#F87B4F" />
              <stop offset="100%" stopColor="#6B3A5E" />
            </linearGradient>
            <linearGradient id="groundgrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2D1F2F" />
              <stop offset="100%" stopColor="#0D0A14" />
            </linearGradient>
            <radialGradient id="sunglow" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#FFE8B0" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#FFE8B0" stopOpacity="0" />
            </radialGradient>
          </defs>

          <rect x={0} y={0} width={800} height={290} fill="url(#skygrad)" />
          <circle cx={600} cy={190} r={200} fill="url(#sunglow)" />
          <circle cx={600} cy={190} r={38} fill="#FFE4A0" opacity={0.95} />

          <polygon
            points="0,290 130,210 230,245 350,180 470,240 590,190 710,225 800,205 800,290"
            fill="#4D2F3E"
            opacity={0.75}
          />
          <polygon
            points="0,295 180,255 280,275 400,230 520,270 640,250 800,265 800,300"
            fill="#2D1F2F"
            opacity={0.9}
          />

          <rect x={0} y={290} width={800} height={160} fill="url(#groundgrad)" />

          <ellipse cx={400} cy={260} rx={30} ry={36} fill="#0A0612" />
          <path
            d="M 368 290 Q 370 300 372 310 L 372 450 L 428 450 L 428 310 Q 430 300 432 290 Q 400 278 368 290 Z"
            fill="#0A0612"
          />

          <text
            x={400}
            y={405}
            textAnchor="middle"
            fontFamily="Inter"
            fontWeight={700}
            fontSize={38}
            fill="white"
            opacity={0.92}
            letterSpacing={4}
          >
            AEGIS
          </text>

          {hotspots.map((hs) => (
            <rect
              key={hs.obj}
              x={hs.x}
              y={hs.y}
              width={hs.w}
              height={hs.h}
              rx={hs.rx}
              className={clsx(
                'cursor-pointer transition-all',
                selectedObject === hs.obj
                  ? '[stroke:theme(colors.accent.DEFAULT)] [stroke-width:2.5] [stroke-dasharray:6_3] [fill:rgba(59,130,246,0.08)] animate-[selectPulse_2s_infinite]'
                  : '[fill:transparent] [stroke:transparent] [stroke-width:2] hover:[stroke:rgba(59,130,246,0.6)] hover:[stroke-dasharray:6_4]',
              )}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedObject(hs.obj);
              }}
            />
          ))}
        </svg>
      </div>

      <div className="bg-bg-panel border border-border rounded-[10px] px-3.5 py-2 flex items-center gap-3.5">
        <button
          onClick={() => setToast('Spelar upp demoscen...')}
          className="w-[34px] h-[34px] rounded-[7px] bg-bg-elevated border border-border hover:bg-accent hover:border-accent flex items-center justify-center text-text transition-all"
        >
          <Icon name="play" size={14} />
        </button>
        <div className="font-mono text-xs text-text-muted font-medium">00:00:08</div>
        <div className="flex-1 h-[5px] bg-bg-elevated rounded-[3px] relative cursor-pointer">
          <div className="absolute left-0 top-0 bottom-0 rounded-[3px] w-[35%] bg-gradient-to-r from-accent to-ai" />
          <div className="absolute left-[35%] top-1/2 -translate-x-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white rounded-full shadow-[0_2px_10px_rgba(59,130,246,0.5)]" />
        </div>
        <div className="font-mono text-xs text-text-muted font-medium">00:00:58</div>
        <div className="w-px h-5 bg-border" />
        <button className="btn btn-ghost" title="Ljud">
          <Icon name="volume-2" size={16} />
        </button>
        <button className="btn btn-ghost" title="Fullskarm">
          <Icon name="maximize" size={16} />
        </button>
      </div>
    </main>
  );
}
